import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { useExperiencesByHotelId } from 'app/hooks/data/extranet/hotel/useExperiencesByHotelId';
import { useRoomsByHotelId } from 'app/hooks/data/extranet/hotel/useRoomsByHotelId';
import { useGetHotelPackages } from 'app/hooks/data/packages/usePackageExtranet';
import type {
  ExtranetExperience,
  ExtranetPackage,
  ExtranetRoom,
} from 'app/typings/packages';

import { HotelContext } from './HotelContext';

type LegacyHotelContextType = {
  rooms: ExtranetRoom[];
  packages: ExtranetPackage[];
  publishedPackages: ExtranetPackage[];
  packagesLoading: boolean;
  roomsLoading: boolean;
  isDayPackage: (packageId: number) => boolean;
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[];
  getRoomsForPackage: (packageId?: number) => ExtranetRoom[];
  getPackagesForRoom: (roomId: number) => ExtranetPackage[];
  roomsFromPublishedPackages: ExtranetRoom[];
  hasAtLeastOneDayPackage: boolean;
};

const defaultLegacyHotelContext: LegacyHotelContextType = {
  rooms: [],
  packages: [],
  publishedPackages: [],
  packagesLoading: false,
  roomsLoading: false,
  isDayPackage: () => false,
  getExperiencesForPackage: () => [],
  getRoomsForPackage: () => [],
  getPackagesForRoom: () => [],
  roomsFromPublishedPackages: [],
  hasAtLeastOneDayPackage: false,
};

export const LegacyHotelContext = createContext<LegacyHotelContextType>(
  defaultLegacyHotelContext
);

type LegacyHotelContextProviderProps = {
  children: ReactNode;
};

export const LegacyHotelContextProvider = ({
  children,
}: LegacyHotelContextProviderProps) => {
  const { hotelId } = useContext(HotelContext);

  const { data: packagesData, isFetching: packagesLoading } =
    useGetHotelPackages(hotelId);
  const { data: roomsData, isFetching: roomsLoading } =
    useRoomsByHotelId(hotelId);
  const { data: experiencesData } = useExperiencesByHotelId(hotelId);

  const experiencesByPackage = packagesData?.packages.reduce(
    (acc, pkg) => {
      acc[pkg.id] =
        experiencesData?.experiences?.filter((exp) =>
          pkg?.experienceIds.includes(exp.id)
        ) || [];

      return acc;
    },
    {} as Record<number, ExtranetExperience[]>
  );

  const roomsByPackage = packagesData?.packages.reduce(
    (acc, pkg) => {
      acc[pkg.id] =
        roomsData?.rooms?.filter((room) => pkg?.roomIds.includes(room.id)) ||
        [];

      return acc;
    },
    {} as Record<number, ExtranetRoom[]>
  );

  const getExperiencesForPackage = (
    packageId: number
  ): ExtranetExperience[] => {
    return (
      experiencesByPackage?.[packageId].sort(
        (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
      ) ?? []
    );
  };

  const getRoomsForPackage = (packageId?: number): ExtranetRoom[] => {
    if (!packageId) {
      return [];
    }

    return roomsByPackage?.[packageId] ?? [];
  };

  const packages =
    packagesData?.packages.map((pkg) => ({
      ...pkg,
      roomIds: undefined,
      experienceIds: undefined,
      rooms: getRoomsForPackage(pkg.id),
      experiences: getExperiencesForPackage(pkg.id),
    })) ?? [];

  const publishedPackages = packages.filter((pkg) => pkg.published) ?? [];

  const isDayPackage = (packageId: number): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const pkg = packages.find((pkg) => pkg.id === packageId);

    return pkg?.dayPackage ?? false;
  };

  const getPackagesForRoom = (roomId: number): ExtranetPackage[] => {
    return packages.filter(
      (pkg) => pkg.rooms.some((room) => room.id === roomId) && pkg.published
    );
  };

  const rooms =
    roomsData?.rooms.sort(
      (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
    ) ?? [];

  const roomsFromPublishedPackages = rooms.filter((room) =>
    publishedPackages.some((pkg) =>
      pkg.rooms.find((publishedRoom) => publishedRoom.id === room.id)
    )
  );

  const hasAtLeastOneDayPackage = publishedPackages.some(
    (pkg) => pkg.dayPackage
  );

  return (
    <LegacyHotelContext.Provider
      value={{
        rooms,
        packages,
        publishedPackages,
        packagesLoading,
        roomsLoading,
        isDayPackage,
        getExperiencesForPackage,
        getRoomsForPackage,
        getPackagesForRoom,
        roomsFromPublishedPackages,
        hasAtLeastOneDayPackage,
      }}
    >
      {children}
    </LegacyHotelContext.Provider>
  );
};
